import { useQuery } from "@tanstack/react-query";
import { useBlockNumber } from "wagmi";
import { isClaimed } from "../actions/claim";
import { isWithdrawn } from "../actions/withdraw";
import { getBridgeTxFailedHistory, getBridgeTxHistory } from "../state/bridgeTxHistory";
import { getShieldingTxHistory } from "../state/shieldingTxHistory";
import { getTransferTxHistory } from "../state/transferTxHistory";
import { getL2ToL1MessageLeafId } from "@aztec/stdlib/messaging";

export type TxHistoryLog = NonNullable<ReturnType<typeof useTxHistoryQuery>["data"]>[number];

export function useTxHistoryQuery() {
  const { data: blockNumber } = useBlockNumber();

  return useQuery({
    queryKey: ["contract_events", String(blockNumber)],
    queryFn: async () => {
      const txHistory = [
        ...getBridgeTxHistory().map((t) => ({ ...t, source: "bridge" }) as const),
        ...getBridgeTxFailedHistory().map((t) => ({ ...t, source: "bridgeFailed" }) as const),
        ...getShieldingTxHistory().map((t) => ({ ...t, source: "shielding" }) as const),
        ...getTransferTxHistory().map((t) => ({ ...t, source: "transfer" }) as const),
      ];

      const txHistorySorted = txHistory.toSorted((a, b) => b.timestamp - a.timestamp);

      const results = await Promise.allSettled(
        txHistorySorted.map(async (savedTxData) => {
          if (savedTxData.source === "bridge") {
            let claimed: boolean;

            if (savedTxData.bridgeType === "withdraw" && savedTxData.state === "claimed") {
              claimed = true;
            } else if (savedTxData.bridgeType === "withdraw" && savedTxData.state === "unclaimed") {
              const messageLeafId = getL2ToL1MessageLeafId({ leafIndex: savedTxData.witness.leafIndex, siblingPath: savedTxData.witness.siblingPath });
              claimed = await isWithdrawn(Number(savedTxData.l2BlockNumber), messageLeafId);
            } else if (savedTxData.bridgeType === "deposit" && savedTxData.state === "claimed") {
              claimed = true;
            } else if (savedTxData.bridgeType === "deposit" && savedTxData.state === "unclaimed") {
              claimed = await isClaimed(savedTxData.hash);
            } else {
              claimed = false;
            }

            const claimTxHash = savedTxData.state === "claimed" ? savedTxData.claimTxHash : undefined;

            return {
              ...savedTxData,
              claimed,
              claimTxHash,
              isManualClaim: false, // TODO: temporary hack
            };
          }
          return savedTxData;
        }),
      );

      return results.filter((r) => r.status === "fulfilled").map((r) => r.value);
    },
  });
}
